import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Button,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Label as AliasIcon,
  ViewModule as SectionIcon,
  BarChart as StatsIcon,
  RateReview as ReviewIcon,
} from '@mui/icons-material';
import { Library, LibraryStats } from '@/types/api';
import { formatDate } from '@/utils/dateUtils';

interface LibraryCardProps {
  library: Library;
  stats?: LibraryStats;
  onEdit?: (library: Library) => void;
  onDelete?: (library: Library) => void;
  onManageAliases?: (library: Library) => void;
  onManageSections?: (library: Library) => void;
  onReview?: (library: Library) => void;
}

const LibraryCard: React.FC<LibraryCardProps> = ({
  library,
  stats,
  onEdit,
  onDelete,
  onManageAliases,
  onManageSections,
  onReview,
}) => {
  const { t } = useTranslation('components');
  return (
    <Card>
      <CardContent>
        {/* Library Name */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ direction: 'rtl', textAlign: 'right' }}>
              {library.canonical_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {library.slug}
            </Typography>
          </Box>
          {onDelete && (
            <Tooltip title={t('libraryCard.deleteTooltip')}>
              <IconButton
                size="small"
                color="error"
                onClick={() => onDelete(library)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Notes */}
        {library.notes && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
            {library.notes}
          </Typography>
        )}

        {/* Statistics */}
        {stats && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
              <Chip
                icon={<StatsIcon />}
                label={t('libraryCard.stats.entries', { count: stats.total_entries })}
                size="small"
                variant="outlined"
              />
              <Chip
                icon={<SectionIcon />}
                label={t('libraryCard.stats.sections', { count: stats.section_count })}
                size="small"
                variant="outlined"
              />
              <Chip
                icon={<AliasIcon />}
                label={t('libraryCard.stats.aliases', { count: stats.alias_count })}
                size="small"
                variant="outlined"
              />
            </Box>

            {/* Entry breakdown by status */}
            {stats.total_entries > 0 && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {stats.entries_by_status.PUBLISHED > 0 && (
                  <Chip
                    label={t('libraryCard.stats.published', { count: stats.entries_by_status.PUBLISHED })}
                    size="small"
                    color="success"
                  />
                )}
                {stats.entries_by_status.REVIEWED > 0 && (
                  <Chip
                    label={t('libraryCard.stats.reviewed', { count: stats.entries_by_status.REVIEWED })}
                    size="small"
                    color="info"
                  />
                )}
                {stats.entries_by_status.DRAFT > 0 && (
                  <Chip
                    label={t('libraryCard.stats.draft', { count: stats.entries_by_status.DRAFT })}
                    size="small"
                    color="default"
                  />
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Metadata */}
        <Typography variant="caption" color="text.secondary">
          {t('libraryCard.created', { date: formatDate(library.created_at, 'PP') })}
        </Typography>
      </CardContent>

      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {onReview && (
            <Button
              size="small"
              variant="contained"
              startIcon={<ReviewIcon />}
              onClick={() => onReview(library)}
            >
              {t('libraryCard.actions.review')}
            </Button>
          )}
          {onEdit && (
            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => onEdit(library)}
            >
              {t('libraryCard.actions.edit')}
            </Button>
          )}
          {onManageAliases && (
            <Button
              size="small"
              startIcon={<AliasIcon />}
              onClick={() => onManageAliases(library)}
            >
              {t('libraryCard.actions.aliases')}
            </Button>
          )}
          {onManageSections && (
            <Button
              size="small"
              startIcon={<SectionIcon />}
              onClick={() => onManageSections(library)}
            >
              {t('libraryCard.actions.sections')}
            </Button>
          )}
        </Box>
      </CardActions>
    </Card>
  );
};

export default LibraryCard;
